import { Component } from '@theme/component';

const CONTEXT_KEYS = ['np_return', 'np_product'];

/** Validate a narrow storefront route before using it as return context. */
export function safeStorefrontPath(value, kind, origin, rootUrl = '/') {
  if (typeof value !== 'string' || !value || value.length > 4096) return null;
  try {
    const url = new URL(value, origin);
    const root = new URL(rootUrl, origin).pathname.replace(/\/$/, '');
    if (url.origin !== origin || url.username || url.password || !['https:', 'http:'].includes(url.protocol)) return null;
    if (root && !url.pathname.startsWith(`${root}/`)) return null;
    const path = root ? url.pathname.slice(root.length) : url.pathname;
    const sample = /^\/pages\/[^/]+\/?$/.test(path) && /^nr-sample-\d{2}$/.test(url.searchParams.get('view') || '');
    const matches = kind === 'list' ? /^\/(?:collections(?:\/[^/]+)?|search)\/?$/.test(path)
      : kind === 'product' ? /^\/products\/[^/]+\/?$/.test(path) || sample
        : /^\/pages\/[^/]+\/?$/.test(path) && !sample;
    if (!matches) return null;
    for (const key of CONTEXT_KEYS) url.searchParams.delete(key);
    if (kind === 'product') {
      for (const key of [...url.searchParams.keys()]) if (!['view', 'variant'].includes(key)) url.searchParams.delete(key);
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch { return null; }
}

/** Resolve per-link context; no storage shared by tabs and no history.back(). */
export function resolvePageContext({ href, referrer, productUrl, origin, rootUrl = '/' }) {
  const url = new URL(href, origin);
  return {
    list: safeStorefrontPath(url.searchParams.get('np_return'), 'list', origin, rootUrl)
      || safeStorefrontPath(referrer, 'list', origin, rootUrl),
    product: safeStorefrontPath(productUrl, 'product', origin, rootUrl)
      || safeStorefrontPath(url.searchParams.get('np_product'), 'product', origin, rootUrl),
  };
}

export function supportUrl(href, context, origin, rootUrl = '/') {
  const path = safeStorefrontPath(href, 'help', origin, rootUrl);
  if (!path) return null;
  const url = new URL(path, origin);
  const list = safeStorefrontPath(context.list, 'list', origin, rootUrl);
  const product = safeStorefrontPath(context.product, 'product', origin, rootUrl);
  if (list) url.searchParams.set('np_return', list);
  if (product) url.searchParams.set('np_product', product);
  return `${url.pathname}${url.search}${url.hash}`;
}

class NuzzlePageLinks extends Component {
  #controller;
  #scope;
  #contentObserver;
  #contentTarget;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#scope = this.closest('[data-nuzzle-page-context]') || this.closest('main') || this;
    this.#scope.addEventListener('click', this.#jump, { signal });
    window.addEventListener('hashchange', this.#refresh, { signal });
    window.addEventListener('popstate', this.#refresh, { signal });
    window.addEventListener('pageshow', this.#refresh, { signal });
    document.addEventListener('shopify:section:load', this.#refresh, { signal });
    this.#refresh();
  }

  updatedCallback() { super.updatedCallback(); this.#refresh(); }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#contentObserver?.disconnect();
    this.#contentObserver = null;
    this.#contentTarget = null;
    document.body?.classList.remove('np-product-content-visible');
    super.disconnectedCallback();
  }

  #refresh = () => {
    const rootUrl = this.dataset.rootUrl || '/';
    const context = resolvePageContext({ href: location.href, referrer: document.referrer, productUrl: this.dataset.productUrl, origin: location.origin, rootUrl });
    if (this.dataset.kind === 'help') this.hidden = !context.list && !context.product;
    if (context.list && this.dataset.kind !== 'help') {
      const current = new URL(location.href);
      if (current.searchParams.get('np_return') !== context.list) {
        current.searchParams.set('np_return', context.list);
        try { history.replaceState(history.state, '', current); } catch { /* Referrer context still works for this visit. */ }
      }
    }
    const list = this.querySelector('[data-return-list]');
    if (list instanceof HTMLAnchorElement) list.hidden = !context.list;
    if (list instanceof HTMLAnchorElement && context.list) { list.href = context.list; list.textContent = `← ${this.dataset.resultsLabel}`; }
    const product = this.querySelector('[data-return-product]');
    if (product instanceof HTMLAnchorElement) product.hidden = !context.product;
    if (product instanceof HTMLAnchorElement && context.product) {
      const url = new URL(context.product, location.origin);
      if (context.list) url.searchParams.set('np_return', context.list);
      product.href = `${url.pathname}${url.search}${url.hash}`;
      product.hidden = false;
    }
    const returns = this.querySelector('.np-page-path__returns');
    if (returns instanceof HTMLElement) returns.hidden = ![list, product].some((link) => link instanceof HTMLAnchorElement && !link.hidden);
    for (const link of this.#scope.querySelectorAll('[data-nuzzle-support] a[href], a[data-nuzzle-support][href]')) {
      const href = supportUrl(link.getAttribute('href'), context, location.origin, rootUrl);
      if (href) link.setAttribute('href', href);
    }
    const nativeTargets = {
      media: '[data-testid="product-information-media"]',
      details: '[data-testid="product-information-details"]',
      help: '.np-product-guide',
    };
    for (const link of this.querySelectorAll('[data-native-target]')) {
      const target = this.#scope.querySelector(nativeTargets[link.dataset.nativeTarget]);
      if (!(target instanceof HTMLElement)) { link.hidden = true; continue; }
      if (!target.id) target.id = `np-native-${link.dataset.nativeTarget}`;
      target.classList.add('np-jump-target');
      link.setAttribute('href', `#${target.id}`);
      link.hidden = false;
    }
    const nativeNav = this.querySelector('.np-page-jumps');
    if (nativeNav) nativeNav.hidden = !nativeNav.querySelector('a:not([hidden])');
    this.#watchProductContent();
    this.#markCurrent();
  };

  #watchProductContent() {
    if (this.dataset.kind === 'help' || typeof IntersectionObserver !== 'function') return;
    const target = this.#scope.querySelector('.nr-product-layout, .product-information');
    if (target === this.#contentTarget) return;
    this.#contentObserver?.disconnect();
    this.#contentTarget = target;
    document.body?.classList.remove('np-product-content-visible');
    if (!target) return;
    // Keep rewards mounted, with its entry clear of product photos and customization controls.
    this.#contentObserver = new IntersectionObserver(entries => {
      document.body?.classList.toggle('np-product-content-visible', entries.some(entry => entry.isIntersecting));
    });
    this.#contentObserver.observe(target);
  }

  #markCurrent() {
    for (const link of this.#scope.querySelectorAll('[data-page-jump]')) {
      if (link.hash === location.hash && location.hash) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    }
  }

  #jump = (event) => {
    if (!(event.target instanceof Element) || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    const link = event.target.closest('a[data-page-jump]');
    if (!(link instanceof HTMLAnchorElement) || !this.#scope.contains(link)) return;
    let id;
    try { id = decodeURIComponent(link.hash.slice(1)); } catch { return; }
    const target = document.getElementById(id);
    if (!(target instanceof HTMLElement) || !this.#scope.contains(target)) return;
    event.preventDefault();
    if (target instanceof HTMLDetailsElement) target.open = true;
    for (let parent = target.parentElement; parent && parent !== this.#scope; parent = parent.parentElement) if (parent instanceof HTMLDetailsElement) parent.open = true;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'start' });
    const url = new URL(location.href);
    url.hash = id;
    try { history.replaceState(history.state, '', url); } catch { /* Native page content remains reachable. */ }
    this.#markCurrent();
  };
}

if (!customElements.get('nuzzle-page-links')) customElements.define('nuzzle-page-links', NuzzlePageLinks);
